import React from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { Flex } from "@jsq/ds/core";
import { globalEnv } from "~/global-env";
import { User, UserRoles } from "~/types/user";
import {
  getRedirectPath,
  isDataStudioUser,
  OPCO_IMPORT_ROLES,
  DATA_STUDIO_FUND_ADMIN_MANAGER_ROLE,
  DATA_STUDIO_USER_ROLE,
  DATA_STUDIO_MANAGER_ROLE,
} from "~/utils/userRoles";
import { ArenaCreation } from "./ArenaCreation";
import ArenaLock from "./ArenaLock";
import CrmImport from "./CrmImport";
import Dashboard from "./Dashboard";
import OpcoImport from "./OpcoImport";
import * as styles from "./styles_vanilla.css";

interface Props {
  currentUser: User;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: string[];
  userRoles: UserRoles;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  userRoles,
}) => {
  const hasAccess = allowedRoles.some((role) => userRoles?.includes(role));

  if (!hasAccess) {
    return <Redirect to="/data-studio/dashboard" />;
  }

  return <>{children}</>;
};

export const DataStudio: React.FC<Props> = ({ currentUser }) => {
  if (!isDataStudioUser(currentUser.roles)) {
    return <Redirect to={getRedirectPath(currentUser)} />;
  }

  return (
    <Flex
      data-testid="data-studio-root"
      flexDirection="row"
      className={styles.wrapperContainer}
    >
      <Flex flexDirection="column" className={styles.contentWrapper}>
        <div className={styles.contentArea} data-testid="data-studio-content-area">
          <Switch>
            <Route exact path="/data-studio/dashboard">
              <Dashboard />
            </Route>
            <Route path="/data-studio/crm-import">
              <ProtectedRoute
                allowedRoles={[DATA_STUDIO_USER_ROLE, DATA_STUDIO_MANAGER_ROLE]}
                userRoles={currentUser.roles}
              >
                <CrmImport />
              </ProtectedRoute>
            </Route>
            <Route exact path="/data-studio/arena-creation">
              <ProtectedRoute
                allowedRoles={[DATA_STUDIO_USER_ROLE, DATA_STUDIO_MANAGER_ROLE]}
                userRoles={currentUser.roles}
              >
                <ArenaCreation />
              </ProtectedRoute>
            </Route>
            <Route exact path="/data-studio/arena-lock">
              <ProtectedRoute
                allowedRoles={[
                  DATA_STUDIO_FUND_ADMIN_MANAGER_ROLE,
                  DATA_STUDIO_USER_ROLE,
                  DATA_STUDIO_MANAGER_ROLE,
                ]}
                userRoles={currentUser.roles}
              >
                <ArenaLock />
              </ProtectedRoute>
            </Route>
            <Route exact path="/data-studio/opco-import">
              <ProtectedRoute
                allowedRoles={[
                  DATA_STUDIO_FUND_ADMIN_MANAGER_ROLE,
                  DATA_STUDIO_USER_ROLE,
                  DATA_STUDIO_MANAGER_ROLE,
                  ...OPCO_IMPORT_ROLES,
                ]}
                userRoles={currentUser.roles}
              >
                <OpcoImport />
              </ProtectedRoute>
            </Route>
            <Route path={globalEnv.dataStudioRoute}>
              <Redirect to="/data-studio/dashboard" />
            </Route>
          </Switch>
        </div>
      </Flex>
    </Flex>
  );
};

export default DataStudio;
